import {CommonModule} from '@angular/common';
import {Component, DestroyRef, ElementRef, HostListener, inject, signal} from '@angular/core';
import {takeUntilDestroyed} from '@angular/core/rxjs-interop';
import {FormControl, ReactiveFormsModule} from '@angular/forms';
import {Router} from '@angular/router';
import {catchError, distinctUntilChanged, map, of, switchMap, tap, timer} from 'rxjs';
import {GlobalSearchByWinOls} from '../../../models/GlobalSearchByWinOls-model';
import {GlobalSearchService} from '../../../service/global-search.service';

@Component({
  selector: 'app-dashboard-winols-search',
  standalone: true,
  imports: [CommonModule, ReactiveFormsModule],
  templateUrl: './dashboard-winols-search.component.html',
  styleUrls: ['./dashboard-winols-search.component.css']
})
export class DashboardWinolsSearchComponent {
  private readonly searchService = inject(GlobalSearchService);
  private readonly router = inject(Router);
  private readonly destroyRef = inject(DestroyRef);
  private readonly host = inject(ElementRef<HTMLElement>);

  readonly searchControl = new FormControl('', {nonNullable: true});
  readonly results = signal<GlobalSearchByWinOls[]>([]);
  readonly isOpen = signal(false);
  readonly isLoading = signal(false);
  readonly hasError = signal(false);
  readonly totalResults = signal(0);

  constructor() {
    this.searchControl.valueChanges.pipe(
      map(value => this.cleanInput(value)),
      distinctUntilChanged(),
      tap(query => {
        this.results.set([]);
        this.totalResults.set(0);
        this.hasError.set(false);
        this.isOpen.set(query.length >= 3);
        this.isLoading.set(query.length >= 3);
      }),
      // switchMap cancels an earlier request as soon as the input changes.
      switchMap(query => query.length < 3
        ? of({content: [] as GlobalSearchByWinOls[], totalElements: 0, failed: false})
        : timer(300).pipe(
            switchMap(() => this.searchService.searchByWinOls(query.toLowerCase(), 0, 10)),
            map(page => ({content: page.content, totalElements: page.totalElements, failed: false})),
            catchError(() => of({content: [] as GlobalSearchByWinOls[], totalElements: 0, failed: true}))
          )),
      takeUntilDestroyed(this.destroyRef)
    ).subscribe(response => {
      this.results.set(response.content);
      this.totalResults.set(response.totalElements);
      this.hasError.set(response.failed);
      this.isLoading.set(false);
    });
  }

  @HostListener('document:click', ['$event'])
  onDocumentClick(event: MouseEvent): void {
    if (!this.host.nativeElement.contains(event.target as Node)) {
      this.isOpen.set(false);
    }
  }

  onFocus(): void {
    if (this.cleanInput(this.searchControl.value).length >= 3) {
      this.isOpen.set(true);
    }
  }

  onKeydown(event: KeyboardEvent): void {
    if (event.key === 'Escape') {
      this.isOpen.set(false);
    } else if (event.key === 'Enter' && this.results().length > 0) {
      event.preventDefault();
      this.openResult(this.results()[0]);
    }
  }

  clearSearch(): void {
    this.searchControl.setValue('');
    this.isOpen.set(false);
  }

  openResult(result: GlobalSearchByWinOls): void {
    if (result.tableName === 'PURE_ORIGINAL_BASE') {
      const matchingIds = result.matchingInfos?.map(info => info.pureOriginalMatchingId).join(',');
      this.router.navigate(['/app/pure-originals/pure-original-details'], {
        queryParams: {
          pureOriginalId: result.id,
          ...(matchingIds ? {matchingIds} : {})
        }
      });
    } else if (result.tableName === 'MASTER') {
      this.router.navigate(['/app/master-details', result.id]);
    } else if (result.tableName === 'VERSION' && result.masterId) {
      this.router.navigate(['/app/master-details', result.masterId], {
        queryParams: {versionName: result.versionName?.trim()}
      });
    } else {
      return;
    }
    this.clearSearch();
  }

  resultLabel(result: GlobalSearchByWinOls): string {
    switch (result.tableName) {
      case 'PURE_ORIGINAL_BASE': return 'Pure Original';
      case 'MASTER': return 'Master';
      case 'VERSION': return 'Version';
      default: return result.tableName;
    }
  }

  resultReference(result: GlobalSearchByWinOls): string {
    if (result.tableName === 'VERSION') {
      return [result.masterWinOlsReference, result.versionName].filter(Boolean).join(' / ') || 'Version';
    }
    return result.winOlsReference || result.winOlsDbName || 'WinOLS reference unavailable';
  }

  resultDetail(result: GlobalSearchByWinOls): string {
    if (result.tableName === 'PURE_ORIGINAL_BASE' && result.winOlsDbName && result.winOlsDbName !== result.winOlsReference) {
      return result.winOlsDbName;
    }
    return `ID ${result.tableName === 'VERSION' ? result.masterId : result.id}`;
  }

  trackResult(_index: number, result: GlobalSearchByWinOls): string {
    return `${result.tableName}-${result.id}`;
  }

  private cleanInput(value: string): string {
    const trimmed = value.trim();
    return trimmed.toLowerCase().endsWith('.ols') ? trimmed.slice(0, -4) : trimmed;
  }
}
